import { useState, useEffect } from 'react';
import { useBroker } from '../contexts/BrokerContext';
import { useLocale } from '../contexts/LocaleContext';
import { Plus, Edit, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import AccountGroupForm from '../components/forms/AccountGroupFormModal';

function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

export default function AccountGroups() {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const { fetchAccountGroups, createAccountGroup, updateAccountGroup, deleteAccountGroup } = useBroker();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const response = await fetchAccountGroups();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
      toast.error(t('settings.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGroupModal  = (group = null) => { setEditingGroup(group); setShowGroupModal(true); };
  const handleCloseGroupModal = () => { setShowGroupModal(false); setEditingGroup(null); };

  const handleSaveGroup = async (groupData) => {
    try {
      if (editingGroup) { 
        await updateAccountGroup(editingGroup.id, groupData); 
        toast.success(t('settings.groupUpdatedSuccess')); 
      }
      else { 
        await createAccountGroup(groupData); 
        toast.success(t('settings.groupCreatedSuccess')); 
      }
      handleCloseGroupModal();
      loadGroups();
    } catch (error) { 
      toast.error(error.message || t('settings.failedToSave')); 
    }
  };

  const handleDeleteGroup = async (group) => {
    if (group.is_default) { 
      toast.error(t('settings.cannotDeleteDefault')); 
      return; 
    }
    if (!confirm(t('settings.confirmDelete').replace('{name}', group.name))) return;
    try { 
      await deleteAccountGroup(group.id); 
      toast.success(t('settings.groupDeletedSuccess')); 
      loadGroups(); 
    }
    catch (error) { 
      toast.error(error.message || t('settings.failedToDelete')); 
    }
  };

  // ── MOBILE ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* Hero */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('accountGroups.title')}
          </p>
          <p className="text-4xl font-black text-white leading-none font-mono">{groups.length}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {groups.filter(g => g.is_active).length} {t('settings.activeGroups')} · {groups.filter(g => g.is_default).length} {t('settings.defaultGroups')}
          </p>
        </div>

        {/* Add button */}
        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <button onClick={() => handleOpenGroupModal()}
            className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            <Plus className="w-4 h-4" />{t('settings.addNewGroup')}
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{t('settings.noGroupsFound')}</p>
            <button onClick={() => handleOpenGroupModal()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white"
              style={{ backgroundColor: 'var(--accent-primary)' }}>
              <Plus className="w-4 h-4" />{t('settings.createFirstGroup')}
            </button>
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-3">
            {groups.map(group => (
              <MobileGroupCard 
                key={group.id} 
                group={group} 
                onEdit={handleOpenGroupModal} 
                onDelete={handleDeleteGroup}
                t={t} />
            ))}
          </div>
        )}

        {showGroupModal && (
          <AccountGroupForm group={editingGroup} onClose={handleCloseGroupModal} onSave={handleSaveGroup} />
        )}
        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2">{t('accountGroups.title')}</h1>
          <p className="text-[var(--text-secondary)]">{t('accountGroups.groupDetails')}</p>
        </div>
        <button onClick={() => handleOpenGroupModal()}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-hover)] transition-smooth">
          <Plus className="w-4 h-4" />{t('settings.addNewGroup')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[var(--text-secondary)]">{t('common.loading')}</p>
          </div>
        </div>
      ) : groups.length === 0 ? (
        <div className="p-12 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-center">
          <p className="text-[var(--text-secondary)] mb-4">{t('settings.noGroupsFound')}</p>
          <button onClick={() => handleOpenGroupModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-hover)] transition-smooth">
            <Plus className="w-4 h-4" />{t('settings.createFirstGroup')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(group => (
            <GroupCard 
              key={group.id} 
              group={group} 
              onEdit={handleOpenGroupModal} 
              onDelete={handleDeleteGroup}
              t={t} />
          ))}
        </div>
      )}

      {showGroupModal && (
        <AccountGroupForm group={editingGroup} onClose={handleCloseGroupModal} onSave={handleSaveGroup} />
      )}
    </div>
  );
}

// ── Mobile group card ─────────────────────────────────────────────────────────
function MobileGroupCard({ group, onEdit, onDelete, t }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>

      {/* Tap-to-expand header */}
      <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left" onClick={() => setExpanded(e => !e)}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
          {group.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{group.name}</p>
            {group.is_default && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                {t('settings.default')}
              </span>
            )}
          </div>
          {group.description && (
            <p className="text-[11px] truncate mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{group.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: group.is_active ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)' : 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)',
              color: group.is_active ? 'var(--accent-success)' : 'var(--text-tertiary)',
            }}>
            {group.is_active ? t('common.active') : t('common.inactive')}
          </span>
          <ChevronDown className="w-4 h-4 transition-transform duration-200"
            style={{ color: 'var(--text-tertiary)', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }} />
        </div>
      </button>

      {/* Always-visible quick stats */}
      <div className="grid grid-cols-3 gap-px border-t"
        style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--border-color)' }}>
        {[
          { label: t('settings.marginCall'), value: `${group.margin_call_level}%`  },
          { label: t('settings.stopOut'),    value: `${group.stop_out_level}%`     },
          { label: t('settings.maxPositions').split(' ')[0], value: group.max_open_positions },
        ].map(({ label, value }) => (
          <div key={label} className="flex flex-col items-center py-2.5"
            style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <p className="text-sm font-black font-mono" style={{ color: 'var(--text-primary)' }}>{value}</p>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Expanded section */}
      {expanded && (
        <div className="px-4 py-3 border-t space-y-4"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 40%, transparent)' }}>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>
              {t('accountGroups.pricingOverrides')}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: t('instruments.spread'), value: group.spread_markup !== null ? `${group.spread_markup}p` : t('groupPricing.brokerDefault') },
                { label: t('instruments.commission'), value: group.commission_per_lot !== null ? `$${group.commission_per_lot}` : t('groupPricing.brokerDefault') },
                { label: t('instruments.leverage'), value: group.max_leverage !== null ? `1:${group.max_leverage}` : t('groupPricing.brokerDefault') },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-xl p-2.5 text-center border"
                  style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                  <p className="text-xs font-black font-mono" style={{ color: 'var(--text-primary)' }}>{value}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => onEdit(group)}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5"
              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
              <Edit className="w-3.5 h-3.5" />{t('common.edit')}
            </button>
            {!group.is_default && (
              <button onClick={() => onDelete(group)}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)', border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
                <Trash2 className="w-3.5 h-3.5" />{t('common.delete')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Desktop group card ────────────────────────────────────────────────────────
function GroupCard({ group, onEdit, onDelete, t }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-6 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] card-hover">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold">{group.name}</h3>
            {group.is_default && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-[var(--accent-primary)]/20 text-[var(--accent-primary)]">
                {t('settings.default')}
              </span>
            )}
            <span className={`px-2 py-1 rounded text-xs font-medium ${group.is_active ? 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]' : 'bg-[var(--text-tertiary)]/20 text-[var(--text-tertiary)]'}`}>
              {group.is_active ? t('common.active') : t('common.inactive')}
            </span>
          </div>
          {group.description && <p className="text-sm text-[var(--text-secondary)]">{group.description}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setExpanded(!expanded)} 
            className="p-2 hover:bg-[var(--bg-tertiary)] rounded-lg transition-smooth" 
            title={expanded ? t('common.collapse') : t('common.expand')}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={() => onEdit(group)} 
            className="p-2 hover:bg-[var(--bg-tertiary)] rounded-lg transition-smooth" 
            title={t('settings.editGroup')}>
            <Edit className="w-4 h-4" />
          </button>
          {!group.is_default && (
            <button onClick={() => onDelete(group)} 
              className="p-2 hover:bg-[var(--accent-danger)]/20 text-[var(--accent-danger)] rounded-lg transition-smooth" 
              title={t('settings.deleteGroup')}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-[var(--border-color)]">
        <div>
          <p className="text-xs text-[var(--text-secondary)] mb-1">{t('settings.marginCall')}</p>
          <p className="font-mono font-semibold">{group.margin_call_level}%</p>
        </div>
        <div>
          <p className="text-xs text-[var(--text-secondary)] mb-1">{t('settings.stopOut')}</p>
          <p className="font-mono font-semibold">{group.stop_out_level}%</p>
        </div>
        <div>
          <p className="text-xs text-[var(--text-secondary)] mb-1">{t('settings.maxPositions')}</p>
          <p className="font-mono font-semibold">{group.max_open_positions}</p>
        </div>
      </div>
      {expanded && (
        <div className="mt-4 pt-4 border-t border-[var(--border-color)] space-y-4">
          <div>
            <h4 className="text-sm font-semibold mb-3">{t('accountGroups.pricingOverrides')}</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-[var(--text-secondary)] mb-1">{t('instruments.spreadMarkup')}</p>
                <p className="font-mono">
                  {group.spread_markup !== null ? `${group.spread_markup} ${t('units.pips')}` : t('groupPricing.brokerDefault')}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)] mb-1">{t('instruments.commission')}</p>
                <p className="font-mono">
                  {group.commission_per_lot !== null ? `$${group.commission_per_lot}/${t('units.lot')}` : t('groupPricing.brokerDefault')}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)] mb-1">{t('settings.maxLeverage')}</p>
                <p className="font-mono">
                  {group.max_leverage !== null ? `1:${group.max_leverage}` : t('groupPricing.brokerDefault')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}